import ConfigUtils, {  DeploymentType, IConfig, SourceApp } from "../../../Config";
import { IProductSalesInfo } from "../../../newSearch/models/IProductSalesInfo";
import WebUtils from "../../../services/WebUtils";
import { IProductDescription } from "../../../ts/models/product/IProductDescription";
import { ILpSeriesInfo } from "../../../ts/providers/IProductProvider";
import { ProductProvider } from "../../../ts/providers/ProductProvider";
import { ProductProviderWeb } from "../../../ts/providers/ProductProviderWeb";
import ManningContentUtils from "../../../ts/utils/ManningContentUtils";
import { ProductUtils } from "../../../ts/utils/ProductUtils";
import ValueUtils from "../../../ts/utils/ValueUtils";
import { INewsAndRecentlyRead } from "../../models/INewsAndRecentlyRead";
import { INewsItem } from "../../models/INewsItem";
import { IProductForum } from "../../models/IProductForum";
import { IManningActivitySummary } from "../../models/ManningActivitySummary";
import { IOwnedProduct } from "../../models/OwnedProduct";
import { IManningOnlineProvider } from "./IManningOnlineProvider";
import { ManningOnlineUtils } from "./ManningOnlineUtils";

interface IRawActivitySummary {
    id: number;
    dateCreated: number;
    lastUpdated: number;
    lastVisitDate: number; //1624545931205
    monthByMonthChange: number; 
    totalSessions: number;
    totalTimeOnSiteInMinutes: number;
    usagePercentile: number;
    userId: number;
}

interface IRawNewsAndRecentlyRead {
    success: boolean;
    news: IRawNewsItem[];
    recentlyRead: IRawProduct[];
}

interface IRawNewsItem {
    id: number;
    description: string;
    title: string;
    summary: string;
    date: string;
    whatsNew: string;
    whatsNext: string;
    subject: string;

    type: "meapUpdate" | "productRelease" | "trendingTitle" | "forumUpdate";

    product: {
        id: number;
        externalId: string;
        slug: string;
        title: string;
        imageUrl: string;
        isLiveVideo: boolean;
        isLiveBook: boolean;
        isLiveProject: boolean;
    }

    amazonReleaseNumber?: number;
    amazonLink?: string;

    readingLengthInMinutes?: number;

    link?: string;
}

interface IRawProductList {
    title: string;
    products: IRawProduct[];
    lastUpdated: string;
    success: boolean;
}

interface IRawProduct {
    id: number;
    preTitle?: string;
    title: string;
    
    authorshipDisplay: string;
    description: string;
    expectedPublishDate: number
    externalId: string;
    hasAudio: boolean;
    
    imageUrl: string;
    isLiveProject: boolean;
    isLiveProjectSeries: boolean;
    isLiveVideo: boolean;
    isbn: string;
    lastSignificantDate: number;
    livebook: boolean;

    publishedDate: number
    seoKeywords: string;
    slug: string;
    tags: string[];

    isMeap: boolean;
    percentageRead?: number;
    
    authorTalk?: boolean;
    conference?: boolean;
    summaryLV?: boolean;
    productWalkthrough?: boolean;
    videoEdition?: boolean;
}

interface IRawOwnedProduct {
    purchaseDate: string;//"2017-12-29T03:50:37-0500",
    formats: string;//"EPUB,KINDLE,LIVEBOOK,PDF",
    hasBeenCancelled: boolean;
    orderId: number | null;
    endDate: string | null;
    authorshipDisplay: string;
    defaultFormats: string;//"PDF,EPUB,KINDLE,LIVEBOOK",
    type: string;//"COMPLIMENTARY",
    productOfferingId: number;
    productExternalId: string;
    imageUrl: string;
    newestFileVersion: number;
    liveaudioAddonProductOfferingId: number | null;
    sectionStartDate: string;
    sectionEndDate: string;
    slug: string;
    productType: string;//"EBOOK",
    isLivebook: boolean;
    productId: number;
    
    productLastUpdated: string;
    lineItemId: number | null;
    sectionId: number | null;
    productTitle: string;
    
    isMeap: boolean;
    
    isLiveVideo: boolean;
    isLiveProject: boolean;
    isLiveProjectSeries: boolean;
    
    forumUrl: string;
    publicSourceCodeDownloadUrl: string;
    startDate: string;
    tags: string[];

    lastSignificantDate: string;
}
export class ManningOnlineWebProvider implements IManningOnlineProvider {
    private webUtils: WebUtils;
    public config: IConfig;
    public deploymentType: DeploymentType;
    private productProvider: ProductProvider;
    public sourceApp: SourceApp;
    
    constructor(sourceApp: SourceApp, deploymentType: DeploymentType){
        this.sourceApp = sourceApp;
        this.config = ConfigUtils.getConfig(sourceApp, deploymentType);
        this.deploymentType = deploymentType;
        this.webUtils = new WebUtils(this.config);
        this.productProvider = new ProductProvider(new ProductProviderWeb(this.webUtils));
    }

    public getLpSeriesInfo(): Promise<ILpSeriesInfo[]> {
        return new Promise<ILpSeriesInfo[]>((resolve, reject) => {
            this.webUtils.ajax(
				this.webUtils.getAbsoluteBackendUrl("/manningOnline/lpSeriesInfo"),
                (data: ILpSeriesInfo[]) => resolve(data),
                error => {
                    console.log(error);
                    resolve([]);
                },
                {}, 
                false, true
            );
        });
    }
    
    public getProductSalesInfo(): Promise<IProductSalesInfo[]> {
        return new Promise<IProductSalesInfo[]>((resolve, reject) => {
            this.webUtils.ajax(
				this.webUtils.getAbsoluteBackendUrl("/manningOnline/productSalesInfo"),
                (data: IProductSalesInfo[]) => resolve(data),
                error => reject(error),
                {}, 
                false, true
            );
        });
    }
    
    public getRecommendedReading(): Promise<IProductDescription[]> {
        return new Promise<IProductDescription[]>((resolve, reject) => {
            this.webUtils.ajax(
				this.webUtils.getAbsoluteBackendUrl("/manningOnline/recommendedReading"),
                (data: IRawProduct[]) => {
                    resolve((data || []).map(it => this.convertRawProduct(it)));
                },
                error => reject(error),
                {}, 
                false, true
            );
        });
    }
    
    public getNewsAndRecentlyRead(): Promise<INewsAndRecentlyRead> {
        return new Promise<INewsAndRecentlyRead>((resolve, reject) => {
            this.webUtils.ajax(
				this.webUtils.getAbsoluteBackendUrl("/manningOnline/newsAndRecentlyRead"),
                (data: IRawNewsAndRecentlyRead) => {
                    if(data && data.success){
                        resolve({
                            news: this.convertNews(data.news),
                            recentReading: (data.recentlyRead || []).map(it => this.convertRawProduct(it))
                        });
                    }
                    else {
                        reject();
                    }
                },
                error => reject(error),
                {}, 
                false, true
            );
        });
    }
    
    public getProductForum(productId: number): Promise<IProductForum> {
        return Promise.reject("Method not implemented");
    }

    public getProductsPercentageFinished(productIds: number[]): Promise<{[productId: number]: number}> {
        return new Promise<{[productId: number]: number}>((resolve, reject) => {
            this.webUtils.ajax(
				this.webUtils.getAbsoluteBackendUrl("/manningOnline/headerInfo"),
                (data) => {
                    if(data && data.success){
                        resolve(data);
                    }
                    else {
                        reject();
                    }
                },
                error => reject(error),
                {
                    productIds: productIds
                }, 
                false, true
            );
        });
    }

    public getProductDescription(productId: number): Promise<IProductDescription> {
        return new Promise<IProductDescription>((resolve, reject) => {
            this.webUtils.ajax(
				this.webUtils.getAbsoluteBackendUrl("/manningOnline/productDescription"),
                (data: IRawProduct) => resolve(this.convertRawProduct(data)),
                error => reject(error),
                {
                    productId: productId
                }, false, true
            );
        });
    }
    
    private convertNews(news: IRawNewsItem[]): INewsItem[] {
        return (news || []).map(it => this.convertNewsItem(it));
    }
    
    private convertNewsItem(item: IRawNewsItem): INewsItem {
        return {
            id: item.id,
            
            product: {
                id: item.product.id,
                manningId: item.product.id,
                coverPath: (item.product.imageUrl || "").indexOf("http") == 0 ? item.product.imageUrl : ManningContentUtils.adjustCoverPath(ManningContentUtils.getCoverPath(item.product.imageUrl), 172, 216),
                title: item.product.title,
            },

            type: item.type,
            
            description: item.whatsNew || ManningOnlineUtils.extractDescriptionFromSummary(item.summary) || item.subject,
            title: ManningOnlineUtils.fixNewsTitle(item.title, item.product.title),
            
            link: ManningOnlineUtils.expandLinkWithChapterLink(item.link, item.title) || ProductUtils.getContentLink(item.product, this.config) || ProductUtils.getProductLink(item.product, this.config),

            amazonReleaseNumber: item.amazonReleaseNumber,
            amazonLink: item.amazonLink,

            date: item.date,
            readingLengthInMinutes: item.readingLengthInMinutes
        };
    }
    
    private convertRawProduct(item: IRawProduct): IProductDescription {
        return {
            id: `${item.id}`,
            manningId: item.id,
            externalId: item.externalId,
            coverPath: ManningContentUtils.adjustCoverPath(ManningContentUtils.getCoverPath(item.imageUrl), 172, 216),
            date: ValueUtils.getDateAsString(new Date(item.lastSignificantDate)),
            dateObj: new Date(item.lastSignificantDate),
            description: ManningContentUtils.getCleanHtml(item.description),
            isLiveaudio: item.hasAudio,
            price: 0,
            priceWithoutDiscount: 0,
            productType: item.isLiveProjectSeries ? "series" : item.isLiveProject ? "project" : item.isLiveVideo ? "video" : "book",
            title: item.title,
            preTitle: item.preTitle,
            authors: item.authorshipDisplay,
            isExternalProduct: false,
            isFreeProduct: false,
            isTwoLineTitle: false,
            isWideCover: item.authorTalk || item.conference || (item as any).isAuthorTalk || (item as any).isConference,
            link: ProductUtils.getProductLink(item, this.config),
            contentLink: ProductUtils.getContentLink(item, this.config),
            marketplaceLink: ProductUtils.getMarketplaceLink(item, this.config),
            rating: 0,
            tags: item.tags,
            isMeap: item.isMeap,
            percentageRead: item.percentageRead
        };
    }
    
    private manningActivitySummaryPromise: Promise<IManningActivitySummary>;
    public getManningActivitySummary(): Promise<IManningActivitySummary> {
        if(this.manningActivitySummaryPromise != null) {
            return this.manningActivitySummaryPromise;
        }
        
        return this.manningActivitySummaryPromise = new Promise<IManningActivitySummary>((resolve, reject) => {
            this.webUtils.ajax(
				this.webUtils.getAbsoluteBackendUrl("/manningOnline/activitySummary"),
                (data: IRawActivitySummary) => {
                    resolve({
                        joinDate: ValueUtils.getDateDay(data.dateCreated ? new Date(data.dateCreated) : new Date()),
                        lastVisitDate: ValueUtils.getDateDay(data.lastVisitDate ? new Date(data.lastVisitDate): new Date()),
                        monthByMonthActivityChange: data.monthByMonthChange || 0,
                        timeOnSiteInMinutes: data.totalTimeOnSiteInMinutes || 0,
                        totalSessions: data.totalSessions || 1,
                        usagePercentile: data.usagePercentile || 99
                    });
                },
                error => {
                    this.manningActivitySummaryPromise = null;
                    reject(error);
                },
                {}, false, true
            );
        });
    }
    
    private allProductsPromise: Promise<IProductDescription[]>;
    public getAllProducts(): Promise<IProductDescription[]> {
        if(this.allProductsPromise != null) {
            return this.allProductsPromise;
        }
        
        return this.allProductsPromise = new Promise<IProductDescription[]>((resolve, reject) => {
            this.productProvider.getProductInfos(false, true).then(productInfos => {
                if(productInfos == null) { productInfos = []; }
                const productDescriptions = ProductUtils.convertProductInfosToProductDescription(productInfos.filter(it => !it.isBundle));
                productDescriptions.sort((a, b) => +b.dateObj - +a.dateObj);
                resolve(productDescriptions);
            });
        });
    }

    private ownedProductsPromise: Promise<IOwnedProduct[]>;
    public getOwnedProducts(): Promise<IOwnedProduct[]> {
        if(this.ownedProductsPromise != null) {
            return this.ownedProductsPromise;
        }
        
        return this.ownedProductsPromise = new Promise<IOwnedProduct[]>((resolve, reject) => {
            this.webUtils.ajax(
				this.webUtils.getAbsoluteBackendUrl("/manningOnline/licenses"),
                (data: IRawOwnedProduct[]) => {
                    if(data && data.length){
                        let ownedProducts = data.map(it => this.convertRawOwnedProduct(it)).sort((a, b) => {
                            return +b.productLastSignificantDate - +a.productLastSignificantDate;
                        });

                        const alreadyAddedIds: {[productId: number]: boolean} = {};
                        const uniqueProducts:IOwnedProduct[] = [];
                        
                        for(const it of ownedProducts){
                            if(alreadyAddedIds[it.id]){ continue; }

                            uniqueProducts.push(it);
                            alreadyAddedIds[it.id] = true;
                        }

                        resolve(uniqueProducts);
                    }
                    else {
                        resolve([]);
                    }
                },
                error => {
                    this.ownedProductsPromise = null;
                    reject(error);
                },
                {}, false, true
            );
        });
    }
    
    private convertRawOwnedProduct(item: IRawOwnedProduct): IOwnedProduct {     
        let productType = "book";
        if(item.isLiveProject) { productType = "project"; }   
        else if(item.isLiveVideo) { productType = "video"; }
        else if(item.isLivebook) { productType = "book"; }
        else {
            if(item.isLiveProjectSeries || (item.imageUrl && item.imageUrl.indexOf("liveProjectSeries") >= 0)){
                productType = "series";
            }
        }
        
        return {
            id: item.productId,
            authors: item.authorshipDisplay,
            coverPath: ManningContentUtils.adjustCoverPath(ManningContentUtils.getCoverPath(item.imageUrl), 172, 216),
            licenseDate: ValueUtils.getDateFromString(item.startDate),
            productLastSignificantDate: ValueUtils.getDateFromString(item.productLastUpdated || item.purchaseDate || item.startDate),
            link: ProductUtils.getProductLink({isLiveBook: item.isLivebook, isLiveProject: item.isLiveProject || productType == "project", isLiveVideo: item.isLiveVideo, slug: item.slug, isOwned: true, externalId: item.productExternalId}, this.config),
            productType: productType as any,
            title: item.productTitle,
            codeDownloadLink: item.publicSourceCodeDownloadUrl,
            forumUrl: item.forumUrl,
            liveaudioOfferingId: item.liveaudioAddonProductOfferingId,
            isLiveaudio: item.liveaudioAddonProductOfferingId != null && item.liveaudioAddonProductOfferingId > 0,
            tags: item.tags,
            licenses: (item.formats || item.defaultFormats || "").split(",").filter(it => it != "LIVEBOOK" && it != "SOLO" && it != "MENTORED" && it != "LIVEVIDEO").map(it => { 
                if(it == "AUDIO") {
                    return {
                        format: it,
                        link: `https://www.manning.com/dashboard/downloadAudio?externalId=${item.productExternalId || (item as any).externalId}`
                    };
                }
                
                return {
                    format: it,
                    link: `https://www.manning.com/dashboard/download?productId=${item.productId}&downloadFormat=${it}`
                }   
            })
        };
    }
}